import { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useQuery } from "@tanstack/react-query"
import i18n from "@/i18n"
import { ButtonLink } from "@/shared/ui/ButtonLink"
import { useNbrAuth } from "@/app/auth/useNbrAuth"
import { listNotifications } from "@/shared/api/rgb"
import { CABINET_ENTRY_PATH } from "@/widgets/cabinet/cabinetNav"

const headerIconButtonClass = "border-0 inline-flex items-center justify-center w-10 h-10"

export type NavbarAuthAreaProps = {
  headerTone: "transparent" | "solid" | "onAccent"
  /** На публичном сайте — вход в последний кабинет. В шапке кабинета переключение уже на чипах. */
  showCabinetLink?: boolean
}

export const NavbarAuthArea = ({ headerTone, showCabinetLink = true }: NavbarAuthAreaProps) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const auth = useNbrAuth()
  const nextLang = i18n.language?.startsWith("en") ? "ru" : "en"
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const notifications = useQuery({
    queryKey: ["notifications", "badge"],
    queryFn: () => listNotifications({ page: 1, page_size: 20 }),
    enabled: auth.isAuthenticated,
    staleTime: 30_000,
  })
  const unread = notifications.data?.unread ?? 0
  const onLight = headerTone === "solid"
  const linkClass =
    headerTone === "transparent"
      ? "text-white hover:text-white drop-shadow"
      : onLight
        ? "text-lib-text hover:text-lib-accent"
        : "text-white hover:text-white"

  useEffect(() => {
    setUserMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!userMenuOpen) return
    const onPointer = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return
      if (menuRef.current?.contains(event.target)) return
      setUserMenuOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setUserMenuOpen(false)
    }
    document.addEventListener("pointerdown", onPointer)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", onPointer)
      document.removeEventListener("keydown", onKey)
    }
  }, [userMenuOpen])

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg ${
          onLight ? "text-gray-700 hover:bg-gray-100" : "text-white hover:bg-white/10"
        }`}
        onClick={() => void i18n.changeLanguage(nextLang)}
        aria-label={t("common.language")}
      >
        {nextLang === "en" ? "EN" : "RU"}
      </button>
      {auth.isAuthenticated ? (
        <>
          <ButtonLink
            to="/account/notifications"
            variant="navPillOutline"
            pillSize="iconAlways"
            headerTone={headerTone}
            className={`${headerIconButtonClass} relative`}
            isActive={pathname.startsWith("/account/notifications")}
            aria-label={t("account.notifications.open")}
          >
            <i className="ri-notification-3-line text-lg" aria-hidden />
            {unread > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-brand-500 text-[10px] leading-4 text-white text-center">
                {unread > 9 ? "9+" : unread}
              </span>
            ) : null}
          </ButtonLink>
          {showCabinetLink ? (
            <ButtonLink
              to={CABINET_ENTRY_PATH}
              variant="navPillOutline"
              headerTone={headerTone}
              className="hidden md:inline-flex"
              isActive={pathname.startsWith("/cabinet") || pathname.startsWith("/account") || pathname.startsWith("/writer") || pathname.startsWith("/union") || pathname.startsWith("/admin") || pathname.startsWith("/analytics") || pathname.startsWith("/portal")}
            >
              {t("nav.account")}
            </ButtonLink>
          ) : null}
          <div className="relative hidden md:block" ref={menuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((open) => !open)}
              className={`font-nav flex items-center gap-2 px-3 py-2 text-[15px] font-semibold tracking-wide transition-colors ${linkClass}`}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label={t("nav.cabinet")}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center ${onLight ? "bg-lib-accent/20 text-lib-accent" : "bg-white/20 text-white"}`}>
                <i className="ri-user-line" aria-hidden />
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${userMenuOpen ? "rotate-180" : ""}`} aria-hidden />
            </button>
            {userMenuOpen ? (
              <div
                className="absolute right-0 top-full mt-2 w-56 bg-lib-card border border-lib-border rounded-xl shadow-lg shadow-lib-primary/10 z-50 overflow-hidden"
                role="menu"
              >
                <Link
                  to="/account/profile"
                  role="menuitem"
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full text-left px-4 py-2.5 text-sm text-lib-text-secondary hover:text-lib-accent hover:bg-lib-bg transition-colors flex items-center gap-2"
                >
                  <i className="ri-user-line" aria-hidden />
                  {t("workspaces.nav.anketa")}
                </Link>
                <Link
                  to="/account/notifications"
                  role="menuitem"
                  onClick={() => setUserMenuOpen(false)}
                  className="w-full text-left px-4 py-2.5 text-sm text-lib-text-secondary hover:text-lib-accent hover:bg-lib-bg transition-colors flex items-center gap-2"
                >
                  <i className="ri-notification-3-line" aria-hidden />
                  {t("nav.notifications")}
                </Link>
                <div className="border-t border-lib-border" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false)
                    void auth.logout()
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-lib-text-secondary hover:text-lib-accent hover:bg-lib-bg transition-colors flex items-center gap-2"
                >
                  <i className="ri-logout-box-r-line" aria-hidden />
                  {t("nav.logout")}
                </button>
              </div>
            ) : null}
          </div>
          <ButtonLink
            to="/account"
            variant="navPillOutline"
            pillSize="iconAlways"
            headerTone={headerTone}
            className={`${headerIconButtonClass} md:hidden`}
            isActive={pathname === "/account" || (pathname.startsWith("/account/") && !pathname.startsWith("/account/notifications"))}
            aria-label={t("nav.cabinet")}
          >
            <i className="ri-user-line text-lg" aria-hidden />
          </ButtonLink>
          <ButtonLink
            variant="navPillOutline"
            pillSize="iconAlways"
            headerTone={headerTone}
            className={`${headerIconButtonClass} md:hidden`}
            onClick={() => auth.logout()}
            aria-label={t("nav.logout")}
          >
            <i className="ri-logout-box-r-line text-lg" aria-hidden />
          </ButtonLink>
        </>
      ) : (
        <button
          type="button"
          onClick={() => auth.login(CABINET_ENTRY_PATH)}
          className={
            onLight
              ? "hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-lib-accent hover:bg-lib-accent-dark text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              : "hidden md:inline-flex items-center gap-2 px-5 py-2.5 border border-white text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
          }
          aria-label={t("nav.login")}
        >
          <i className="ri-login-box-line" aria-hidden />
          {t("nav.login")}
        </button>
      )}
    </div>
  )
}
